import UptimeGeneralDialog from "../../dialogComponents/generalDialog/generalDialog";
import UptimeMarketPlace from "../../MarketPlace/index";
import { Box, Typography, Divider, Grid, Button } from "@mui/material";
import React, { useEffect, memo } from "react";
import { useTheme } from "styled-components";
import DefaultUptimeTheme from "../../theme/defaultTheme";

const SpecificationRow = memo(
  ({ label, value }: { label: string; value: string }) => {
    return (
      <>
        <Box display="flex" alignItems="center">
          <Box flexBasis="50%">
            <Typography variant="h6" color="text.primary">
              {label}
            </Typography>
          </Box>
          <Box flexBasis="50%" padding="16px 0">
            <Typography variant="body1" color="text.secondary">
              {value}
            </Typography>
          </Box>
        </Box>
      </>
    );
  }
);

const NOT_AVAILABLE = "N/A";

const ORDER_ITEM_STATUS = {
  PENDING_APPROVAL: "pending_approval",
  ORDERED: "ordered",
  SHIPPED: "shipped",
  DELIVERED: "delivered",
  CANCELED: "canceled",
  SHIPPED_EXCEPTION: "shipped_exception",
  PENDING: "pending",
  APPROVED: "approved",
  NOT_APPROVED: "not_approved",
};

interface DetailDialogProps {
  product: any;
  onClose: any;
  currentImageProp: any;
  onMarkAsReceived: any;
  locale: any;
}

const DetailDialog = (props: DetailDialogProps) => {
  const theme = useTheme() as unknown as typeof DefaultUptimeTheme;
  const { product, onClose, locale, currentImageProp, onMarkAsReceived } =
    props;
  const [currentImage, setCurrentImage] = React.useState(currentImageProp);
  const [loading, setLoading] = React.useState<Record<string, boolean>>({});

  useEffect(() => {
    setCurrentImage(product?.pictures?.[0]?.large || "");
  }, [product]);

  const updateLoading = (loading: Record<string, boolean>) => {
    setLoading((prevState: any) => ({ ...prevState, ...loading }));
  };

  const handleMarkReceived = async (product: any) => {
    updateLoading({ markReceived: true });
    await onMarkAsReceived(product);
    updateLoading({ markReceived: false });
  };

  const generateActionButtons = () => {
    const { status } = product;
    if (!status) return null;

    switch (status) {
      case ORDER_ITEM_STATUS.SHIPPED: {
        return (
          <UptimeMarketPlace.Button
            sx={{ textTransform: "uppercase" }}
            size="medium"
            color="primary"
            variant="contained"
            loadingStart={loading.markReceived}
            onClick={() => handleMarkReceived(product)}
          >
            {loading.markReceived ? "" : locale?.markAsReceived}
          </UptimeMarketPlace.Button>
        );
      }
      case ORDER_ITEM_STATUS.DELIVERED: {
        return null;
      }

      default: {
        return null;
      }
    }
  };

  return (
    <UptimeGeneralDialog
      content={
        <Box>
          <Grid container>
            <Grid item xs={12}>
              <Typography
                variant="overline"
                color="text.secondary"
                textTransform="uppercase"
              >
                {product?.brand.name}
              </Typography>
              <Typography paddingY={1} variant="h3" color="text.primary">
                {product?.name}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <UptimeMarketPlace.ProductDetailComponents.ImagesContent
                images={product?.pictures || []}
                currentImage={currentImage}
                onClick={(image: string) => setCurrentImage(image)}
              />
            </Grid>
            <Grid item xs={12} mt={"120px"} padding={"24px 0 32px"}>
              <Typography variant="h3" color="text.primary">
                {locale?.productDetail?.specifications}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Divider orientation="horizontal" />
              <Box paddingY={"12px"}>
                <Typography variant="h5" color="text.primary">
                  {locale?.productDetail?.general}
                </Typography>
              </Box>
              <Box marginLeft={`${theme.uptimeSpace[5]}px`}>
                <SpecificationRow
                  label={locale?.productDetail?.branch}
                  value={product?.brand?.name || NOT_AVAILABLE}
                />
                <SpecificationRow
                  label={locale?.productDetail?.type}
                  value={product?.category[0]?.name || NOT_AVAILABLE}
                />
                <SpecificationRow
                  label={locale?.productDetail?.operatingSystem}
                  value="Windows 10 pro 64 (Win11 License)"
                />
                <SpecificationRow
                  label={locale?.productDetail?.keyboard}
                  value="English (US), QWERTY"
                />
                <SpecificationRow
                  label={locale?.productDetail?.powerAdapter}
                  value="US/Canada/Mexico"
                />
                <SpecificationRow
                  label={locale?.productDetail?.graphicCard}
                  value="AMD Radeon™ Graphics"
                />
                <SpecificationRow
                  label={locale?.productDetail?.communications}
                  value="Wi-Fi 6, Bluetooth® 5.0, 3 Cell 45Wh"
                />
                <SpecificationRow
                  label={locale?.productDetail?.warranty}
                  value="3YR Onsite"
                />
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Divider orientation="horizontal" sx={{ padding: "20px 0" }} />
              <Box sx={{ flex: "auto" }}>
                <Box paddingY={"12px"}>
                  <Typography variant="h5" color="text.primary">
                    {locale?.productDetail?.display}
                  </Typography>
                </Box>
                <Box marginLeft={`${theme.uptimeSpace[5]}px`}>
                  <SpecificationRow
                    label={locale?.productDetail?.screenSize}
                    value={
                      product?.specifications?.Display[
                        "specifications.display_size"
                      ] || NOT_AVAILABLE
                    }
                  />
                  <SpecificationRow
                    label={locale?.productDetail?.screenType}
                    value={
                      product?.specifications?.Display[
                        "specifications.display_type"
                      ] || NOT_AVAILABLE
                    }
                  />
                </Box>
              </Box>
              <Box>
                <Divider orientation="horizontal" sx={{ padding: "20px 0" }} />
                <Box paddingY={"12px"}>
                  <Typography variant="h5" color="text.primary">
                    {locale?.productDetail?.processor}
                  </Typography>
                </Box>
                <Box marginLeft={`${theme.uptimeSpace[5]}px`}>
                  <SpecificationRow
                    label={locale?.productDetail?.processorType}
                    value={
                      product?.specifications?.Processor[
                        "specifications.processor_type"
                      ] || NOT_AVAILABLE
                    }
                  />
                  <SpecificationRow
                    label={locale?.productDetail?.processorSpeed}
                    value={
                      product?.specifications?.Processor[
                        "specifications.processor_speed"
                      ] || NOT_AVAILABLE
                    }
                  />
                </Box>
              </Box>
              <Box>
                <Divider orientation="horizontal" sx={{ padding: "20px 0" }} />
                <Box paddingY={"12px"}>
                  <Typography variant="h5" color="text.primary">
                    {locale?.productDetail?.memoryStorage}
                  </Typography>
                </Box>
                <Box marginLeft={`${theme.uptimeSpace[5]}px`}>
                  <SpecificationRow
                    label={locale?.productDetail?.RAMCapacity}
                    value={
                      product?.specifications?.["Memory & Storage"]?.[
                        "specifications.memory"
                      ] || NOT_AVAILABLE
                    }
                  />
                  <SpecificationRow
                    label={locale?.productDetail?.storage}
                    value={
                      product?.specifications?.["Memory & Storage"]?.[
                        "specifications.storage"
                      ] || NOT_AVAILABLE
                    }
                  />
                </Box>
              </Box>
              <Box
                sx={{
                  padding: "30px 0 10px 0",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-end",
                }}
              >
                <Box>
                  {generateActionButtons()}
                  <Button
                    sx={{ textTransform: "uppercase", marginLeft: "12px" }}
                    onClick={() => onClose()}
                    size="medium"
                    color="primary"
                    variant="contained"
                  >
                    {locale?.close}
                  </Button>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Box>
      }
      title={locale?.cardDeviceDetail?.title?.myDeviceDetail}
      maxWidth="sm"
      headerBackgroundDefault={true}
      hideButtonCancel
      buttonSubmit={null}
      handleClose={onClose}
    />
  );
};

export default DetailDialog;
